// src/components/UnlabelledTransferDetailDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import toast from "react-hot-toast";
import { fmtDate } from "../utils/date";

import {
  useRespondUnlabelledTransferMutation,
  useGetUnlabelledTransferByIdQuery,
} from "../toolkitServices/unlabelledApi";
import UnlabelledTransferRejectReasonModal from "./UnlabelledTransferRejectReasonModal";

const StatPill = ({ label, value, color }) => {
  const colorMap = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border ${colorMap[color]}`}
    >
      <span className="text-[10px] uppercase font-semibold">{label}</span>
      <span className="text-xs font-bold">{value}</span>
    </span>
  );
};

const UnlabelledTransferDetailDrawer = ({
  transfer,
  onClose,
  userId,
  userName,
  canRespond = true,
}) => {
  const [itemResponses, setItemResponses] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [rejectingItem, setRejectingItem] = useState(null);
  const [bulkReject, setBulkReject] = useState(false);

  const [respondTransfer, { isLoading }] = useRespondUnlabelledTransferMutation();

  const { data, refetch, isFetching } = useGetUnlabelledTransferByIdQuery(transfer._id);
  const full = data?.data ?? data; // supports both shapes
  const items = full?.items || [];
  const summary = full?.summary || {};

  useEffect(() => {
    if (transfer?._id) refetch();
  }, [transfer?._id, refetch]);

  const isReadOnly = !canRespond;

  // ✅ accept items (single/multi)
  const handleAccept = (ids) => {
    const updates = {};
    ids.forEach((id) => {
      updates[id] = { status: "accepted" };
    });
    setItemResponses((prev) => ({ ...prev, ...updates }));
    setSelectedItems([]);
  };

  // ✅ reject items (opens modal)
  const handleReject = (ids, bulk = false) => {
    setBulkReject(bulk);
    setRejectingItem({ ids });
  };

  const handleRejectSubmit = (reason) => {
    const updates = {};
    rejectingItem?.ids?.forEach((id) => {
      updates[id] = { status: "rejected", reason };
    });
    setItemResponses((prev) => ({ ...prev, ...updates }));
    setRejectingItem(null);
    setSelectedItems([]);
  };

  const toggleSelect = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const itemKey = (it) => String(it?.itemId ?? it?._id ?? it?.id ?? it?.skuId ?? it?.index ?? "");

  const allResponded =
    items.length > 0 &&
    items.every((i, idx) => {
      const k = itemKey(i) || String(idx);
      return itemResponses[k]?.status || i?.status;
    });

  const handleSubmit = async () => {
    if (!allResponded) {
      toast.error("Please accept or reject all items before submitting");
      return;
    }

    const responses = items.map((i, idx) => {
      const k = itemKey(i) || String(idx);
      const v = itemResponses[k] || {};
      const status = v.status || i?.status || "pending";
      return {
        itemId: i?.itemId ?? i?._id ?? i?.id ?? "",
        status,
        rejectionReason: v.reason || i?.rejectionReason || "",
      };
    });

    try {
      await respondTransfer({
        transferId: transfer._id,
        userId,
        userName,
        responses,
      }).unwrap();

      toast.success("Transfer updated successfully");
      onClose?.();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update transfer");
    }
  };

  const acceptedCount = Object.values(itemResponses).filter(
    (x) => x.status === "accepted"
  ).length;
  const rejectedCount = Object.values(itemResponses).filter(
    (x) => x.status === "rejected"
  ).length;

  const transferStatus = transfer?.status || full?.status || "pending";
  const statusClasses =
    transferStatus === "pending"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : transferStatus === "completed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : transferStatus === "rejected"
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : "bg-violet-50 text-violet-700 border-violet-200";

  const pendingSelectableIds = useMemo(() => {
    return items
      .map((it, idx) => itemKey(it) || String(idx))
      .filter((k, idx) => {
        const it = items[idx];
        const currentStatus = itemResponses[k]?.status || it?.status || "pending";
        return currentStatus === "pending";
      });
  }, [items, itemResponses]);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <motion.div
        className="w-full max-w-4xl h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
      >
        {/* HEADER */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 bg-gradient-to-r from-[#f4f1ff] to-white flex justify-between items-start gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-[#4b3d91]">
                Transfer #{transfer._id}
              </h2>
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${statusClasses}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {transferStatus}
              </span>
            </div>

            <p className="text-xs text-gray-600">
              From{" "}
              <span className="font-medium">{transfer.fromUserName || "-"}</span>{" "}
              <span className="mx-1">→</span>
              To <span className="font-medium">{transfer.toUserName || "-"}</span>
            </p>

            <p className="text-[11px] text-gray-500">
              Created on:{" "}
              <span className="font-medium">{fmtDate(transfer.createdAt)}</span>
            </p>
          </div>

          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-slate-200 text-gray-500 hover:text-rose-500 hover:border-rose-200 bg-white shadow-sm"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 flex flex-col px-6 pb-5 pt-4 overflow-hidden">
          {/* SUMMARY */}
          {!isFetching && (
            <div className="mb-4">
              <div className="inline-flex flex-wrap gap-2 text-[11px]">
                <StatPill
                  label="Total"
                  value={summary.total || items.length || 0}
                  color="slate"
                />
                <StatPill
                  label="Accepted"
                  value={summary.accepted || 0}
                  color="emerald"
                />
                <StatPill
                  label="Rejected"
                  value={summary.rejected || 0}
                  color="rose"
                />
                <StatPill
                  label="Pending"
                  value={summary.pending || 0}
                  color="amber"
                />
              </div>
            </div>
          )}

          {/* BULK ACTIONS */}
          {selectedItems.length > 0 && !isReadOnly && (
            <div className="flex justify-between items-center mb-3 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span className="text-gray-600">{selectedItems.length} item(s) selected</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleAccept(selectedItems)}
                  className="px-3 py-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 text-xs font-medium"
                >
                  Accept Selected
                </button>
                <button
                  onClick={() => handleReject(selectedItems, true)}
                  className="px-3 py-1 rounded-full bg-rose-500 text-white hover:bg-rose-600 text-xs font-medium"
                >
                  Reject Selected
                </button>
              </div>
            </div>
          )}

          {/* ITEM TABLE */}
          <div className="flex-1 rounded-lg border border-slate-200 overflow-hidden bg-white">
            <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 text-[#4b3d91] sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-center w-10">
                      {isReadOnly ? null : (
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            setSelectedItems(e.target.checked ? pendingSelectableIds : [])
                          }
                          checked={
                            pendingSelectableIds.length > 0 &&
                            selectedItems.length === pendingSelectableIds.length
                          }
                        />
                      )}
                    </th>
                    {/* <th className="px-4 py-2 text-left">Branch</th> */}
                    <th className="px-4 py-2 text-left">Stock</th>
                    
                    <th className="px-4 py-2 text-right whitespace-nowrap">Qty</th>
                    <th className="px-4 py-2 text-right whitespace-nowrap">Net Wt</th>
                    <th className="px-4 py-2 text-right whitespace-nowrap">Stone Wt</th>
                    <th className="px-4 py-2 text-right whitespace-nowrap">Diamond Wt</th>
                    {/* <th className="px-4 py-2 text-right whitespace-nowrap">Amount</th> */}
                    <th className="px-4 py-2 text-center">Action / Status</th>
                  </tr>
                </thead>

                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-gray-500 text-sm">
                        No items found in this transfer.
                      </td>
                    </tr>
                  )}

                  {items.map((item, idx) => {
                    const k = itemKey(item) || String(idx);
                    const response = itemResponses[k];
                    const currentStatus = response?.status || item?.status || "pending";

                    const title =
                      item?.description ||
                      item?.productName ||
                      item?.itemName ||
                      item?.name ||
                      `Item ${idx + 1}`;

                    const category = item?.categoryName || item?.category || "-";
                    const qty = item?.quantity ?? item?.qty ?? "-";
                    const wt = item?.netWt ?? item?.weight ?? "-";
                    const amt = item?.amount ?? item?.totalAmount ?? item?.mrp ?? "-";

                    return (
                      <tr
                        key={k}
                        className="border-t border-slate-100 hover:bg-violet-50/40"
                      >
                        <td className="text-center px-3 py-2">
                          {isReadOnly ? null : (
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(k)}
                              onChange={() => toggleSelect(k)}
                              disabled={currentStatus !== "pending"}
                            />
                          )}
                        </td>
{/* 
                        <td className="px-4 py-2">
                          <div className="text-xs sm:text-sm text-gray-800">
                            {title}
                          </div>
                          <div className="text-[11px] text-gray-400 font-mono">
                            {item?.itemCode || item?._id || item?.id || ""}
                          </div>
                        </td> */}

                        <td className="px-4 py-2 text-gray-700">{item?.displayName}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{item?.requested?.qty}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{item?.requested?.netWt}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{item?.requested?.stoneWt}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{item?.requested?.diamondWt}</td>
                       

                        <td className="px-4 py-2 text-center">
                          {isReadOnly ? (
                            <span
                              className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${
                                currentStatus === "accepted"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : currentStatus === "rejected"
                                  ? "bg-rose-50 text-rose-700 border border-rose-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {currentStatus === "accepted"
                                ? "Accepted"
                                : currentStatus === "rejected"
                                ? `Rejected${response?.reason ? ` • ${response.reason}` : ""}`
                                : "Pending"}
                            </span>
                          ) : currentStatus === "pending" ? (
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => handleAccept([k])}
                                className="px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] sm:text-xs hover:bg-emerald-600 font-medium"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleReject([k])}
                                className="px-3 py-1 rounded-full bg-rose-500 text-white text-[11px] sm:text-xs hover:bg-rose-600 font-medium"
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span
                              className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${
                                currentStatus === "accepted"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-rose-50 text-rose-700 border border-rose-200"
                              }`}
                            >
                              {currentStatus === "accepted"
                                ? "Accepted"
                                : `Rejected${response?.reason ? ` • ${response.reason}` : ""}`}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* FOOTER */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-[11px] sm:text-sm text-gray-600">
              <span className="mr-3">
                Accepted: <b>{acceptedCount}</b>
              </span>
              <span>
                Rejected: <b>{rejectedCount}</b>
              </span>
              {isReadOnly && (
                <div className="mt-1 text-[10px] text-gray-500">
                  Read-only mode (outgoing / view-only).
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                disabled={isLoading}
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-gray-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
              >
                Close
              </button>

              {!isReadOnly && (
                <button
                  disabled={!allResponded || isLoading}
                  onClick={handleSubmit}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                    allResponded ? "bg-[#6b59c6] hover:bg-[#5a48a8]" : "bg-slate-400 cursor-not-allowed"
                  }`}
                >
                  {isLoading ? "Saving..." : "Submit Response"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Reject reason modal */}
        <AnimatePresence>
          {rejectingItem && !isReadOnly && (
            <UnlabelledTransferRejectReasonModal
              open
              bulk={bulkReject}
              onClose={() => setRejectingItem(null)}
              onSubmit={handleRejectSubmit}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default UnlabelledTransferDetailDrawer;
